/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import _ from "lodash"
import moment from "moment"
import {
  ActivityIndicator,
  Appearance,
  TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
  ImageStyle
} from "react-native"
import { Cell, FloatButton } from "../cycle-water-quality-multi-measurement-list/cycle-water-quality-multi-measurement-list-screen"
import { Icon, Modal, ScrollView, Skeleton, useDisclose, View } from "native-base"
import {
  Button,
  FlatList,
  Image,
  ImageViewing,
  JalaIcon,
  JalaLoading,
  LazyRender,
  ListEmpty,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
  AutoImage
} from "../../../../components"
import { SafeAreaView } from "react-native-safe-area-context"
import { color, nativeBaseTheme, images } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { translate } from "../../../../i18n"
import { useStores } from "../../../../models"
import { delay } from "../../../../utils/delay"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"
import AppIntroSlider from "react-native-app-intro-slider"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const CONTENT_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  paddingBottom: 68
}

const IMAGE = {
  height: 48,
  width: 48,
  borderRadius: 4
}

const SCROLL_VIEW_DETAIL_ITEM: ViewStyle = {
  flex: 1,
  marginBottom: 8
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  width: 120,
  alignSelf: "center",
  borderRadius: 40
}

const IMAGE_DETAIL_ITEM = {
  height: 320,
  borderRadius: 4
}

const EMPTY_NOTES: ImageStyle = {
  resizeMode: 'contain',
  width: 14,
  height: 12,
  alignSelf: 'center'
}

const TableHead: FC<{ colorScheme?: "light" | "dark" }> = observer(function TableHead(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  const renderPlaceholder = useCallback(() => {
    return <Skeleton.Text lines={1} paddingX="8px" paddingY="10px" />
  }, [])

  return <View
    borderWidth="1px"
    borderColor={`gray.${colorScheme === 'light' ? '200' : '800'}`}
    backgroundColor={`gray.${colorScheme === 'light' ? '100' : '900'}`}
    flexDirection="row"
  >
    <LazyRender renderPlaceholder={renderPlaceholder}>
      <Cell text={_.upperFirst(translate('cycleDiseases.disease'))} flex={3} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('cycleDiseases.image'))} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('cycleDiseases.remark'))} width="64px" textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('edit'))} width="48px" padding="0px" alignItems="center" justifyContent="center" textPreset="body3" colorScheme={colorScheme} />
    </LazyRender>
  </View>;
});

const DetailItemModal: FC<{ colorScheme?: "light" | "dark", item: any }> = observer(function DetailItemModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), item = {}, children } = props;
  const { disease, other_disease, logged_at, files = [], remark } = item
  const images = useMemo(() => files.map((file) => file.path_source), [files]);

  const imageIndex = useRef<number>(0)
  const setImageIndex = useCallback((value) => {
    imageIndex.current = value;
  },[])

  const { isOpen, onClose, onOpen } = useDisclose()
  const { isOpen: isOpenImage, onClose: onCloseImage, onOpen: onOpenImage } = useDisclose()

  const handleDisease = useCallback(() => {
    onClose();
    navigate('Home.Stack', 'Home.Disease');
  },[onClose])

  const activeDotStyle = useMemo(() => ({
    backgroundColor: colorScheme === "light" ? color.primary : nativeBaseTheme.colors.light[200]
  }), [colorScheme])

  const dotStyle = useMemo(() => ({
    backgroundColor: colorScheme === "light" ? color.palette.blueJalaLight : nativeBaseTheme.colors.dark[200],
  }), [colorScheme])

  const renderItem = useCallback((info) => {
    const onPress = async () => {
      onClose()
      await delay(1)
      setImageIndex(info.index)
      onOpenImage()
    }

    return <View>
      <TouchableOpacity onPress={onPress}>
        <Image
          source={info.item.path_source}
          resizeMode="contain"
          style={IMAGE_DETAIL_ITEM}
        />
      </TouchableOpacity>
    </View>;
  }, [onClose, onOpenImage, setImageIndex])

  const onPressOpen = useCallback(() => {
    onCloseImage()
    onOpen()
  }, [onCloseImage, onOpen])

  return (<>
    <TouchableOpacity onPress={onPressOpen}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        flex={1}
        flexGrow={1}
        alignSelf="stretch"
        marginY="80px"
        marginX="16px"
        padding="16px"
        rounded="8px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <Text tx="feeds.remark" transformText={_.upperFirst} preset="display2" colorScheme={colorScheme} marginBottom="8px" />
        <Text
          text={`${moment.utc(logged_at).local().format('DD MMM YYYY')}`}
          preset="body2"
          colorScheme={colorScheme}
          marginBottom="8px"
        />
        <ScrollView
          contentContainerStyle={SCROLL_VIEW_DETAIL_ITEM}
        >
          {!!files.length && <View minHeight="336px">
            <AppIntroSlider
              data={files}
              activeDotStyle={activeDotStyle}
              dotStyle={dotStyle}
              renderItem={renderItem}
              showDoneButton={false}
              showNextButton={false}
              showPrevButton={false}
              showSkipButton={false}
            />
          </View>}
          <Text text={disease && (disease.short_name || disease.full_name) || other_disease} preset="title4" marginBottom="4px" colorScheme={colorScheme} />
          {!!disease && !!disease.short_name && <Text text={disease.full_name} preset="body2" colorScheme={colorScheme} />}
          {!!remark && <Text text={remark || "-"} preset="body2" colorScheme={colorScheme} />}
        </ScrollView>
        <Button
          testID="jala:id/disease-button"
          preset="link"
          text={translate('cycleDiseaseListScreen.seeRelatedDiseaseInfo')}
          textPreset="title4"
          colorScheme={colorScheme}
          onPress={handleDisease}
        />
        <View height="12px" />
        <Button
          testID="jala:id/ok-button"
          tx="common.ok"
          preset="primary"
          colorScheme={colorScheme}
          style={BUTTON_DETAIL_ITEM}
          onPress={onClose}
        />
      </View>
    </Modal>
    {!!images.length && <ImageViewing
      images={images}
      imageIndex={imageIndex.current}
      visible={isOpenImage}
      onRequestClose={onCloseImage}
      onImageIndexChange={setImageIndex}
    />}
  </>)
})

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleEdit: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info,
    handleEdit
  } = props;
  const {
    item,
    item: { disease, other_disease, files = [], logged_at, _pushing, is_can_be_pushed, remark }
  } = info;

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  const renderPlaceholder = useCallback(() => {
    return <View>
      <Skeleton.Text lines={1} padding="8px" _line={{ height: '14px' }} />
    </View>
  }, [])

  const onPressEdit = useCallback(() => handleEdit(item), [handleEdit, item])

  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View flexDirection="row" position="relative">
      <Cell flex={3} colorScheme={colorScheme} textColor="primary.700" alignItems="flex-start" paddingX="4px" textProps={{ isTruncated: false }}>
        <Text text={moment.utc(logged_at).local().format('dddd, DD MMM YYYY')} preset="body3" colorScheme={colorScheme} color={`muted.${colorScheme === 'light' ? '700' : '300'}`} />
        <Text text={(disease && disease.full_name) || other_disease} preset="body2" colorScheme={colorScheme} color="primary.700" />
      </Cell>
      <Cell colorScheme={colorScheme} textColor="primary.700">
        <DetailItemModal colorScheme={colorScheme} item={item}>
          {files[0] && <Image
            key={files[0].path}
            source={files[0].path_source}
            resizeMode="cover"
            style={IMAGE}
            {...{alt: files[0].path}}
          /> || <Text text="-" colorScheme={colorScheme} color="primary.700" />}
        </DetailItemModal>
      </Cell>
      <Cell width="64px" paddingY="0px" justifyContent="center" colorScheme={colorScheme} alignItems="center">
        {(remark || files?.length) ? <DetailItemModal colorScheme={colorScheme} item={item}>
          <Icon {...iconBaseStyle} as={JalaIcon} name="comment-1" size="18px" color="primary.500" />
        </DetailItemModal> : <AutoImage source={images.empty_notes} style={EMPTY_NOTES} />}
      </Cell>
      <Cell width="48px" paddingY="0px" justifyContent="center" alignItems="center" colorScheme={colorScheme}>
        {_pushing
          && <ActivityIndicator size={15} />
          || <TouchableOpacity onPress={onPressEdit}>
            <View size="32px" justifyContent="center" alignItems="center">
              <Icon as={JalaIcon} name="pencil" size="12px" color="primary.500"/>
            </View>
        </TouchableOpacity>}
      </Cell>
      {is_can_be_pushed && (
        <Cell
          width="10px" right="0px" top="5px" position="absolute" paddingY="0px"
          justifyContent="center" colorScheme={colorScheme} backgroundColor="blue.200"
        >
          <Icon {...iconBaseStyle} as={JalaIcon} name="cloud-upload" size="10px" color="primary.500" />
        </Cell>
       )}
    </View>
  </LazyRender>;
})

export const CycleCycleDiseaseListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function CycleDiseaseTableScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  // const { someStore, anotherStore } = useStores()
  const {
    appStore,
    appStore: {
      isOffline,
      farmStore: {
        pondStore: {
          cycleStore,
          cycleStore: {
            cycle,
            cycleDiseases: models,
            loadingCycleDisease: loadingModel
          }
        }
      },
      diseases
    },
    cycleDiseaseStore: modelStore,
    cycleDiseaseStore: { pushIdQueue, needRefresh, storing, updating, deleting, modifying },
    fileStore,
    fileStore: { pushIdQueue: pushIdQueueFile, needRefresh: needRefreshFile, deleting: deletingFile },
  } = useStores();

  const [dateTimeFilter, setDateTimeFilter] = useState<{
    date_after?: string,
    date_before?: string,
    time?: string
  }>({})

  const modelFlats = useMemo(() => {
    return models.filter(({ logged_at }) => (
      (!dateTimeFilter.date_after || dateTimeFilter.date_after <= moment.utc(logged_at).local().format('YYYY-MM-DD'))
      && (!dateTimeFilter.date_before || moment.utc(logged_at).local().format('YYYY-MM-DD') <= dateTimeFilter.date_before)
    )).map((m) => ({
      ...m,
      disease: diseases.find((d) => d.id === m.disease_id),
      _pushing: !!pushIdQueue[m.id] || (m.files || []).reduce((prev, cur) => !!pushIdQueueFile[cur.id] || prev, false),
      _loading: loadingModel
    }))
  }, [dateTimeFilter, diseases, loadingModel, models, pushIdQueue, pushIdQueueFile])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - view tabs - disease');
  }, []))

  useEffect(() => {
    modelStore.init();
    fileStore.init();
    cycleStore.getCycleDiseases(!isOffline).catch();
  }, [cycleStore, fileStore, isOffline, modelStore])

  useEffect(() => {
    if (needRefresh && !deletingFile) {
      if (isOffline) {
        cycleStore.getCycleDiseases(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getCycleDiseases(false).then(() => {
          cycleStore.getCycleDiseases(true, () => {
            modelStore.set('needRefresh', null)
            modelStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, deletingFile, isOffline, modelStore, needRefresh])

  useEffect(() => {
    if (needRefreshFile) {
      if (isOffline) {
        cycleStore.getCycleDiseases(false).then(() => {
          fileStore.set('needRefresh', null)
          fileStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getCycleDiseases(false).then(() => {
          cycleStore.getCycleDiseases(true, () => {
            fileStore.set('needRefresh', null)
            fileStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, fileStore, isOffline, needRefreshFile])

  const onAddPress = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - add disease');
    await delay(1);
    cycle && navigate('Home.Stack', 'Home.CycleDisease.Create', { pond_id: cycle.pond_id, cycle_id: cycle.id });
  }, [cycle])

  const handleEdit = useCallback((item) => {
    navigate('Home.Stack', 'Home.CycleDisease.Edit', { ...item })
  }, [])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} handleEdit={handleEdit} />
  }, [colorScheme, handleEdit])

  const keyExtractor = useCallback((item) => `${item.id}`, [])

  const onRefresh = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      cycleStore.getCycleDiseases(!isOffline).catch()
    }).catch()
  }, [appStore, cycleStore, isOffline])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {!!modelFlats.length && <TableHead colorScheme={colorScheme} />}
      <FlatList
        data={modelFlats}
        renderItem={renderItem}
        contentContainerStyle={CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        ListEmptyComponent={(loadingModel || modifying) && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 214}px`}>
            <JalaLoading />
          </View>
        ) || <ListEmpty text={translate("listEmpty.text")} colorScheme={colorScheme} height={`${height - 214}px`} />}
        ListHeaderComponent={(loadingModel || storing) && !!modelFlats.length && !updating && !deleting && (
          <View height="40px" alignItems="center" justifyContent="center" paddingTop="8px">
            <Text tx="fetchingData" preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.muted[colorScheme === 'light' ? '700' : '300']} />
          </View>
        ) || undefined}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={false}
        estimatedItemSize={70}
      />
      {!cycle.finished_at && <FloatButton
        colorScheme={colorScheme}
        dateTimeFilterValue={dateTimeFilter}
        onDateTimeFilterChange={setDateTimeFilter}
        onAddPress={onAddPress}
      />}
    </Screen>
  )
})
